import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bot, Send, User, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [useAi, setUseAi] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    return api.get(`/tickets/${ticketId}`).then(r => setTicket(r.data)).catch(() => setTicket(null));
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const postReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/reply`, { content: reply, use_ai: useAi });
      setTicket(data);
      setReply("");
      toast.success(useAi ? "AI reply posted." : "Reply posted.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try { await api.patch(`/tickets/${ticketId}/status`, { status }); load(); toast.success("Status updated."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!ticket) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const isStaff = user?.role === "admin" || user?.role === "staff";

  return (
    <div className="p-6 lg:p-10 max-w-5xl space-y-8">
      <Link to="/tickets" className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-muted-foreground hover:text-primary transition-colors" data-testid="ticket-back-link"><ArrowLeft className="w-3 h-3" /> {t("tickets.title")}</Link>

      <header className="border border-border rounded-sm bg-card p-6">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <div className="text-[11px] font-mono tracking-widest text-primary">{ticket.ticket_id} · {ticket.language?.toUpperCase()}</div>
            <h1 className="text-2xl font-semibold text-white mt-1" data-testid="ticket-detail-subject">{ticket.subject}</h1>
            <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase mt-2">{ticket.owner_name} · {ticket.owner_email}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={ticket.status} />
            {isStaff && (
              <select value={ticket.status} onChange={e => setStatus(e.target.value)} className="h-9 px-3 text-xs rounded-sm bg-secondary border border-border font-mono tracking-widest uppercase text-white focus:border-primary outline-none" data-testid="ticket-status-select">
                <option value="awaiting_review">awaiting_review</option>
                <option value="open">open</option>
                <option value="in_progress">in_progress</option>
                <option value="resolved">resolved</option>
              </select>
            )}
          </div>
        </div>
      </header>

      <section className="space-y-4" data-testid="ticket-thread">
        {ticket.messages?.map((m, i) => (
          <MessageBubble key={i} m={m} />
        ))}
      </section>

      {ticket.suggested_manuals && (
        <section className="border border-primary/25 bg-primary/5 rounded-sm p-5" data-testid="ticket-manuals">
          <div className="text-overline mb-2 text-primary">SUGGESTED MANUALS</div>
          <pre className="text-xs font-mono text-white/90 whitespace-pre-wrap">{ticket.suggested_manuals}</pre>
        </section>
      )}

      <form onSubmit={postReply} className="border border-border rounded-sm bg-card p-5 space-y-3" data-testid="ticket-reply-form">
        <textarea rows={4} value={reply} onChange={e => setReply(e.target.value)} placeholder={t("tickets.reply.placeholder")}
          className="w-full px-3 py-2 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm text-white"
          data-testid="ticket-reply-textarea" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-[11px] font-mono tracking-widest text-muted-foreground uppercase">
            <input type="checkbox" checked={useAi} onChange={e => setUseAi(e.target.checked)} className="accent-[#00A8FF]" data-testid="ticket-reply-ai-toggle" />
            <Bot className="w-3.5 h-3.5 text-primary" /> {t("tickets.use_ai")}
          </label>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 h-10 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors disabled:opacity-50" data-testid="ticket-reply-submit">
            {busy ? "…" : t("tickets.reply")} <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

function MessageBubble({ m }) {
  const isAi = m.author === "ai";
  const isStaff = m.author === "staff";
  const Icon = isAi ? Bot : isStaff ? Shield : User;
  const color = isAi ? "text-primary border-primary/30 bg-primary/5" : isStaff ? "text-[#00E676] border-[#00E676]/25 bg-[#00E676]/5" : "text-white border-border bg-card";
  return (
    <div className={`border rounded-sm p-5 ${color}`} data-testid={`ticket-msg-${m.author}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.22em] uppercase">
        <Icon className="w-3.5 h-3.5" />
        <span>{m.author_name}</span>
        <span className="text-muted-foreground">· {new Date(m.created_at).toLocaleString()}</span>
      </div>
      <div className="mt-3 text-sm leading-relaxed text-white/90 whitespace-pre-wrap">{m.content}</div>
    </div>
  );
}
